import { Box, styled } from "@mui/material";
import { usePathname } from "next/navigation";
import Icon from "./Icon";
import { JobStatus, JobTypes } from "@lib/enums";
import useMessage from "./hooks/useMessage";
import { useJobStatus } from "@lib/jobs/useJobStatus";
import { useCallback, useEffect, useRef } from "react";
import Quantum from "@components/shared/loading/QuantumLoader";

const Container = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ theme, active }) => ({
  height: active ? "42px" : "0px",
  width: "100%",
  top: "0",
  left: "0",
  position: "fixed",
  overflow: "hidden",
  zIndex: theme.zIndex.drawer + 5,
  transition: "height 0.8s ease",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#ddeafc",
}));

const Content = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  columnGap: "16px",
  height: "100%",
  fontWeight: "600",
  fontSize: "0.8rem",
});

const Links = styled(Box)({
  display: "flex",
  columnGap: "8px",
});

export default function StatusBar({ open, onOpen }) {
  const { jobs, removeJobs } = useJobStatus(JobTypes.AI);
  const pathname = usePathname();

  const handleHide = useCallback((ids) => {
    removeJobs(ids);
    onOpen(false);
  }, []);

  const { showLoader, message, link } = useMessage(jobs, handleHide);

  useEffect(() => {
    onOpen(Boolean(message));
  }, [Boolean(message)]);

  // A completed import shows a "Success!" message in the bar. It's purely
  // informational, so navigating to a new route is a natural, reliable signal
  // to clear it. Only COMPLETE jobs are dismissed — in-progress imports keep
  // running and failed jobs stay until the user acknowledges them.
  const previousPathname = useRef(pathname);
  useEffect(() => {
    if (pathname === previousPathname.current) {
      return;
    }
    previousPathname.current = pathname;
    const completedIds = jobs
      .filter((job) => job.status === JobStatus.COMPLETE)
      .map((job) => job.id);
    if (completedIds.length > 0) {
      handleHide(completedIds);
    }
  }, [pathname, jobs, handleHide]);

  return (
    <Container active={open}>
      <Content>
        {showLoader ? <Quantum /> : <Icon type={JobTypes.AI} />}
        <p>{message}</p>
        {link && <Links>{link}</Links>}
        {showLoader && <Quantum />}
      </Content>
    </Container>
  );
}
